import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Link,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { useQuery } from "@tanstack/react-query";
import { Booking } from "../../services/manualBooking";
import {
  FlightFinance,
  RevenueLine,
  formatNaira,
  getApiError,
  getFlightFinance,
} from "../../services/finance";
import RescheduleDrawer from "./RescheduleDrawer";
import AddonsDrawer from "./AddonsDrawer";
import CancelRefundDrawer from "./CancelRefundDrawer";
import AddRevenueDrawer from "../Finance/AddRevenueDrawer";

type ChildDrawer = "reschedule" | "addons" | "cancel" | "revenue" | null;

const revenueStatusSx = (status?: string) => {
  const paid = (status || "").toLowerCase() === "paid";
  return {
    height: 22,
    fontSize: 12,
    borderRadius: "6px",
    bgcolor: paid ? "#ECFDF3" : "#F2F4F7",
    color: paid ? "#12B76A" : "#667085",
  };
};

const RevenueRow = ({
  label,
  amount,
  status,
  action,
  onAction,
}: {
  label: string;
  amount?: number;
  status?: string;
  action?: string;
  onAction?: () => void;
}) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    gap="12px"
    py="12px"
    borderBottom="1px solid #E4E7EC"
  >
    <Box>
      <Typography fontSize={14} color="#101828" mb="4px">
        {label}
      </Typography>
      <Chip
        size="small"
        label={status || "Not Applicable"}
        sx={revenueStatusSx(status)}
      />
    </Box>
    <Box textAlign="right">
      <Typography fontSize={14} fontWeight={600} color="#101828">
        {formatNaira(amount)}
      </Typography>
      {action && onAction && (
        <Link
          component="button"
          onClick={onAction}
          sx={{
            fontSize: 14,
            color: "#101828",
            textDecorationColor: "#101828",
          }}
        >
          {action}
        </Link>
      )}
    </Box>
  </Box>
);

interface BookingDetailsPanelProps {
  open: boolean;
  onClose: () => void;
  booking: Booking | null;
}

const getStatusColor = (status?: string) => {
  const value = (status || "").toLowerCase();
  if (
    [
      "successful",
      "success",
      "confirmed",
      "booked_successfully",
      "completed",
    ].includes(value)
  )
    return "#12B76A";
  if (value === "pending") return "#F79009";
  return "#F04438";
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box
    display="flex"
    flexWrap="wrap"
    justifyContent="space-between"
    alignItems="center"
    gap="8px"
    py="12px"
  >
    <Typography fontSize={14} color="#667085">
      {label}
    </Typography>
    <Typography
      fontSize={14}
      fontWeight={600}
      color="#101828"
      textAlign="right"
    >
      {value}
    </Typography>
  </Box>
);

const BookingDetailsPanel = ({
  open,
  onClose,
  booking,
}: BookingDetailsPanelProps) => {
  const [childDrawer, setChildDrawer] = useState<ChildDrawer>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const financeQuery = useQuery({
    queryKey: ["flightFinance", booking?.id],
    queryFn: () => getFlightFinance(booking!.id),
    enabled: open && Boolean(booking?.id),
    retry: false,
  });
  const finance = financeQuery.data?.data;
  const revenue = finance?.revenue;

  // Close child drawers only when a different booking is selected, not when
  // the same booking is refreshed after a reschedule/cancel.
  useEffect(() => {
    setChildDrawer(null);
  }, [booking?.id]);

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": isMobile
          ? {
              width: "100%",
              maxHeight: "92vh",
              overflowY: "auto",
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
            }
          : {
              width: "420px",
              maxWidth: "100%",
              maxHeight: "100vh",
              overflowY: "auto",
            },
      }}
    >
      {booking && (
        <Box p={{ xs: "16px", sm: "24px" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={600} color="#101828">
              Booking Details
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Chip
            label={booking.status}
            sx={{
              mt: "16px",
              bgcolor: `${getStatusColor(booking.status)}1A`,
              color: getStatusColor(booking.status),
              fontWeight: 600,
              fontSize: "12px",
              textTransform: "capitalize",
            }}
          />

          <Box
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="center"
            gap="8px"
            bgcolor="#F9FAFB"
            border="1px solid #E4E7EC"
            borderRadius="8px"
            p={{ xs: "12px", sm: "16px" }}
            mt="20px"
          >
            <Typography
              fontSize={{ xs: 16, sm: 18 }}
              fontWeight={600}
              color="#101828"
              textAlign="center"
              sx={{ wordBreak: "break-word" }}
            >
              {booking.from || "N/A"}
            </Typography>
            <ArrowRightAltIcon sx={{ color: "#667085" }} />
            <Typography
              fontSize={{ xs: 16, sm: 18 }}
              fontWeight={600}
              color="#101828"
              textAlign="center"
              sx={{ wordBreak: "break-word" }}
            >
              {booking.to || "N/A"}
            </Typography>
          </Box>

          <Divider sx={{ my: "16px" }} />

          <DetailRow label="Reference" value={booking.booking_id} />
          <Divider />
          <DetailRow label="PNR" value={booking.pnr || "N/A"} />
          <Divider />
          <DetailRow label="Airline" value={booking.airline || "N/A"} />
          <Divider />
          <DetailRow
            label="Email"
            value={booking.contact || booking.email || "N/A"}
          />
          <Divider />
          <DetailRow
            label="Amount"
            value={`₦${Number(booking.amount).toLocaleString()}`}
          />
          <Divider />
          <DetailRow
            label="Booking Date"
            value={
              booking.date && !isNaN(new Date(booking.date).getTime())
                ? new Date(booking.date).toLocaleString()
                : "N/A"
            }
          />

          {booking.passengers && booking.passengers.length > 0 && (
            <>
              <Divider />
              <Typography
                fontSize={14}
                fontWeight={600}
                color="#101828"
                pt="16px"
                pb="4px"
              >
                Passengers
              </Typography>
              {booking.passengers.map((passenger, index) => (
                <Box key={index}>
                  <DetailRow
                    label={passenger.ageGroup || `Passenger ${index + 1}`}
                    value={
                      passenger.fullName ||
                      `${passenger.firstName || ""} ${passenger.lastName || ""}`.trim() ||
                      "N/A"
                    }
                  />
                  {index < booking.passengers!.length - 1 && <Divider />}
                </Box>
              ))}
            </>
          )}
          <Divider />

          <Box pt="24px" pb="12px">
            <Typography fontSize={18} fontWeight={600} color="#101828">
              Revenue
            </Typography>
            <Typography fontSize={14} color="#667085" mb="8px">
              Track every revenue stream generated from this booking.
            </Typography>

            {financeQuery.isLoading ? (
              [0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height={56} />)
            ) : financeQuery.isError ? (
              <Box py="12px">
                <Typography fontSize={13} color="#D92D20" mb="8px">
                  {getApiError(
                    financeQuery.error,
                    "Could not load revenue details.",
                  )}
                </Typography>
                <Button size="small" onClick={() => financeQuery.refetch()}>
                  Retry
                </Button>
              </Box>
            ) : (
              finance && (
                <>
                  <RevenueRow
                    label="Amount Paid"
                    amount={finance.amountPaid?.amount}
                    status={finance.amountPaid?.status}
                  />
                  {(
                    [
                      ["serviceCharge", undefined, null],
                      ["rescheduleRevenue", "Reschedule", "reschedule"],
                      ["addons", "View / Add", "addons"],
                      ["cancellationRevenue", "Cancel / Refund", "cancel"],
                      ["wakanowCommission", undefined, null],
                      ["otherRevenue", "Add", "revenue"],
                    ] as [
                      keyof FlightFinance["revenue"],
                      string | undefined,
                      ChildDrawer,
                    ][]
                  ).map(([key, action, drawer]) => {
                    const line: RevenueLine | undefined = revenue?.[key];
                    return (
                      <RevenueRow
                        key={key}
                        label={line?.label || key}
                        amount={line?.amount}
                        status={line?.status}
                        action={action}
                        onAction={
                          drawer ? () => setChildDrawer(drawer) : undefined
                        }
                      />
                    );
                  })}
                  <Box
                    mt="20px"
                    bgcolor="#ECFDF3"
                    border="1px solid #D1FADF"
                    borderRadius="8px"
                    p="16px"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap="8px"
                  >
                    <Typography fontSize={14} color="#101828">
                      Total Booking Revenue:
                    </Typography>
                    <Typography fontSize={22} fontWeight={700} color="#101828">
                      {formatNaira(finance.totalBookingRevenue)}
                    </Typography>
                  </Box>
                </>
              )
            )}
          </Box>
        </Box>
      )}

      {booking && (
        <>
          <RescheduleDrawer
            open={childDrawer === "reschedule"}
            onClose={() => setChildDrawer(null)}
            booking={booking}
            finance={finance}
          />
          <AddonsDrawer
            open={childDrawer === "addons"}
            onClose={() => setChildDrawer(null)}
            booking={booking}
            finance={finance}
          />
          <CancelRefundDrawer
            open={childDrawer === "cancel"}
            onClose={() => setChildDrawer(null)}
            booking={booking}
          />
          <AddRevenueDrawer
            open={childDrawer === "revenue"}
            onClose={() => setChildDrawer(null)}
            bookingRef={booking.pnr || booking.booking_id}
          />
        </>
      )}
    </Drawer>
  );
};

export default BookingDetailsPanel;
